import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { AppointmentType } from '@prisma/client';
import { Appointment, GeneralRequest, LoadedUser } from '../../interfaces';
import './form.css';
import {
  CreateAptValues,
  ImageItem,
  getExistingImgList,
  initFormValues,
} from '../../utils/form/appointmentForm';
import { updateRequestDetails } from '../../services/appointments';
import TattooDetails from './TattooDetails';
import InternalNotes from './InternalNotes';
import ClientSearchAutofill from './ClientSearchAutofill';
import SelectInput from './SelectInput';
import DollarInput from './DollarInput';
import PriceInputs from './PriceInputs';
import useUser from '../../hooks/global/useUser';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useBusinessOrganization from '../../hooks/businesses/useBusinessOrganization';
import {
  getLengthOptions,
  isAppointmentDepositPaid,
  validatePrice,
} from '../../helpers/appointments';
import { AppContext } from '../../context/AppContext/context';

interface Props {
  request: Appointment | GeneralRequest;
  onClose: () => void;
  onSuccess: (
    updated: Partial<Appointment & GeneralRequest>,
  ) => void | Promise<void>;
}

export default function EditRequestForm({
  request,
  onClose,
  onSuccess,
}: Props) {
  const { user: loadedUser } = useUser<LoadedUser>();
  const { updateLoadingModal, closeLoadingModal } = useLoadingModal();
  const { state } = React.useContext(AppContext);
  const { piercingLocations } = state;
  const {
    hasMultipleBusinesses,
    businessOptions,
    getArtistOptionsForBusiness,
    getBusinessById,
  } = useBusinessOrganization();

  const [formError, setFormError] = React.useState('');
  const [images, setImages] = React.useState<ImageItem[]>(() =>
    getExistingImgList(request),
  );

  const isAppointment = 'appointmentId' in request;
  const isAccepted = isAppointment && request.status === 'ACCEPTED';
  const appointment = request as Appointment;
  const isDepositPaid = isAppointment && isAppointmentDepositPaid(appointment);
  const initialBusinessId =
    request.businessId || loadedUser?.business?.id || '';
  const initialEmployeeId = isAppointment ? request.employeeId || '' : '';
  const isForeignBusiness =
    !request.businessId || request.businessId !== loadedUser?.business?.id;
  const canChangeLocation = hasMultipleBusinesses && !isForeignBusiness;

  const initialValues = React.useMemo<CreateAptValues>(() => {
    const values: CreateAptValues = { ...initFormValues };

    values.businessId = initialBusinessId;
    values.employeeId = initialEmployeeId;
    values.tattooLocation = request.tattooLocation || '';
    values.bodySide = request.bodySide || '';
    values.tattooSize = request.tattooSize || '';
    values.tattooColor = request.tattooColor || '';
    values.tattooDetails = request.tattooDetails || '';
    values.internalNotes = request.internalNotes ?? undefined;

    const clientSource = request.customerGuest ?? request.customer;
    values.customerId = request.customerId;
    values.customerGuestId =
      request.customerGuest?.id ?? request.customerGuestId ?? undefined;
    values.firstName = clientSource.firstName ?? '';
    values.lastName = clientSource.lastName ?? undefined;
    values.email = clientSource.email ?? undefined;
    values.phoneNumber = request.customer.phoneNumber.replace('+', '');
    values.dob = clientSource.dob
      ? new Date(clientSource.dob).toISOString().split('T')[0]
      : undefined;

    if (isAppointment) {
      values.serviceId = request.serviceId ? String(request.serviceId) : '0';
      values.piercingLocationId = request.piercingLocationId
        ? String(request.piercingLocationId)
        : '';
      if (request.piercingLocationId) {
        values.locationCategory =
          piercingLocations?.find(
            location => location.locationId === request.piercingLocationId,
          )?.locationCategory || '';
      }

      if (isAccepted) {
        values.appointmentLengthInMinutes = String(
          request.appointmentLengthInMinutes || '',
        );
        values.depositPrice = request.depositPrice || 0;
        if (request.priceType) {
          values.priceType = request.priceType;
        }
        if (values.priceType === 'FIXED') {
          values.price = request.price ?? 0;
        } else if (values.priceType === 'RANGE') {
          values.minPrice = request.minPrice;
          values.maxPrice = request.maxPrice;
        } else {
          values.hourlyPrice = request.hourlyPrice;
        }
      }
    } else {
      values.serviceId = '0';
    }

    return values;
  }, [
    request,
    piercingLocations,
    isAccepted,
    isAppointment,
    initialBusinessId,
    initialEmployeeId,
  ]);

  const appointmentLengthOptions = getLengthOptions({
    user: loadedUser,
    appointmentType: (isAppointment
      ? request.appointmentType || 'NEW_APPOINTMENT'
      : 'NEW_APPOINTMENT') as AppointmentType,
    serviceId:
      isAppointment && request.serviceId
        ? Number(request.serviceId)
        : undefined,
  });

  return (
    <Box
      style={{
        width: 900,
        margin: 'auto',
      }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          setFormError('');

          const isLocationChanging =
            !!values.businessId && values.businessId !== initialBusinessId;

          if (isLocationChanging && isAppointment && !values.employeeId) {
            setFormError('Select an artist at the new location before saving.');
            return;
          }

          if (isAccepted) {
            const priceError = validatePrice(
              values,
              (appointment.appointmentType ||
                'NEW_APPOINTMENT') as AppointmentType,
            );
            if (priceError) {
              setFormError(priceError.error);
              return;
            }
          }

          const newImages = images.filter(i => !i.isUploaded);
          const existingImages = request.refImgsId ?? [];
          const imagesToDelete = existingImages.filter(
            img => !images.some(i => i.url === img),
          );

          updateLoadingModal({
            header: 'Updating Request...',
            message: 'This could take a minute, please do not refresh the page',
            status: 'loading',
          });

          try {
            const response = await updateRequestDetails(
              { ...values, newImages, imagesToDelete },
              request,
            );
            // endpoints return the bare row without the edited client relation
            const clientPatch = values.customerGuestId
              ? {
                  customerGuest: {
                    ...request.customerGuest!,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    dob: values.dob,
                  },
                }
              : {
                  customer: {
                    ...request.customer,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    dob: values.dob,
                    phoneNumber: `+${values.phoneNumber}`,
                  },
                };
            toast.success('Request Updated!');
            onClose();
            await onSuccess({
              ...response.data.result,
              ...clientPatch,
            } as Partial<Appointment & GeneralRequest>);
          } catch (error) {
            setFormError(
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred.',
            );
            toast.error('Error updating request');
          } finally {
            setSubmitting(false);
            closeLoadingModal();
          }
        }}>
        {({ isSubmitting, setFieldValue, setFieldTouched, values }) => {
          const selectedBusinessId = values.businessId || initialBusinessId;
          const isLocationChanging =
            !!values.businessId && values.businessId !== initialBusinessId;
          const artistOptions = canChangeLocation
            ? getArtistOptionsForBusiness(selectedBusinessId)
            : [];
          const showArtistSelect = isAppointment && canChangeLocation;
          const requiresArtist =
            showArtistSelect && (isLocationChanging || !!initialEmployeeId);

          return (
            <>
              <ClientSearchAutofill selectedRequest={request} />
              <Form>
                {formError && (
                  <Typography variant="body1" color="primary">
                    {formError}
                  </Typography>
                )}

                {canChangeLocation && (
                  <div className="input-group appointment-details">
                    <Typography variant="h2" component="h2">
                      Location
                    </Typography>
                    <div className="inputs">
                      <div className="input-row">
                        <SelectInput
                          values={values}
                          setFieldValue={(name, value) => {
                            const previousBusinessId =
                              values.businessId || initialBusinessId;
                            setFieldValue(name, value);
                            if (value && value !== previousBusinessId) {
                              const newBusinessHasEmployee =
                                !!values.employeeId &&
                                !!getBusinessById(
                                  value,
                                )?.employeesToBusinesses.some(
                                  etb => etb.employeeId === values.employeeId,
                                );
                              if (!newBusinessHasEmployee) {
                                setFieldValue('employeeId', '');
                              }
                            }
                          }}
                          name="businessId"
                          label="Location"
                          options={businessOptions}
                          placeholder="Select a location"
                          disabled={false}
                        />
                      </div>
                      {showArtistSelect && (
                        <div className="input-row">
                          <SelectInput
                            values={values}
                            setFieldValue={setFieldValue}
                            name="employeeId"
                            label={requiresArtist ? 'Artist *' : 'Artist'}
                            options={artistOptions}
                            placeholder="Select an artist"
                            disabled={false}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isAccepted && (
                  <div className="input-group appointment-details">
                    <Typography variant="h2" component="h2">
                      Appointment Details
                    </Typography>
                    <div className="inputs">
                      {appointmentLengthOptions && (
                        <div className="input-row">
                          <SelectInput
                            values={values}
                            setFieldValue={setFieldValue}
                            name="appointmentLengthInMinutes"
                            label="Length"
                            options={appointmentLengthOptions}
                            placeholder="Select duration"
                            disabled={false}
                          />
                        </div>
                      )}
                      <div className="input-row">
                        <DollarInput
                          name="depositPrice"
                          label="Deposit Amount"
                          placeholder="Enter Amount"
                          values={values}
                          setFieldValue={setFieldValue}
                          disabled={isDepositPaid}
                          hidden={false}
                          setFieldTouched={setFieldTouched}
                        />
                      </div>
                      <PriceInputs />
                    </div>
                  </div>
                )}

                <TattooDetails images={images} setImages={setImages} />
                <InternalNotes />

                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !values.firstName ||
                    !values.phoneNumber ||
                    (requiresArtist && isLocationChanging && !values.employeeId)
                  }
                  variant="contained"
                  className="primary">
                  Save Changes
                </Button>
              </Form>
            </>
          );
        }}
      </Formik>
    </Box>
  );
}
